import { ExternalLink, Globe } from "lucide-react";
import type { UrlResource } from "@/types/cms";

interface LinkCardProps {
  resource: UrlResource;
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function getCategoryColour(category: string): string {
  switch (category) {
    case "training-posts":
      return "var(--color-acrrm-orange)";
    case "supervisors":
      return "var(--color-acrrm-dark)";
    case "registrars":
      return "#2563eb";
    case "policies":
      return "#7c3aed";
    case "reference":
      return "#059669";
    case "wellbeing":
      return "#e11d48";
    default:
      return "var(--color-acrrm-orange)";
  }
}

function getCategoryLabel(category: string): string {
  switch (category) {
    case "training-posts":
      return "Training Posts";
    case "supervisors":
      return "Supervisors";
    case "registrars":
      return "Registrars";
    case "policies":
      return "Policies & Requirements";
    case "reference":
      return "Reference";
    case "wellbeing":
      return "Wellbeing";
    default:
      return category;
  }
}

export function LinkCard({ resource }: LinkCardProps) {
  const colour = getCategoryColour(resource.category);
  const domain = getDomain(resource.url);

  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col p-4 rounded-xl border bg-card shadow-sm transition-all hover:shadow-md hover:border-[var(--color-acrrm-orange)]/40"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center mt-0.5"
          style={{ backgroundColor: `${colour}15` }}
        >
          <Globe className="h-4.5 w-4.5" style={{ color: colour }} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-semibold text-sm text-foreground leading-snug mb-1 group-hover:text-[var(--color-acrrm-orange)] transition-colors">
            {resource.title}
          </h4>
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-2">
            {resource.description}
          </p>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: `${colour}12`,
                color: colour,
              }}
            >
              {getCategoryLabel(resource.category)}
            </span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <ExternalLink className="h-2.5 w-2.5" />
              {domain}
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}
